import React from 'react';

import { DIGITAL_SERVICE_PROVIDERS } from 'types/constants';
import { ArtistProfile, DspName, DspConnection as Connection } from 'types';
import {
  StepCreatorParams,
  WizardContext,
  WizardStepConfig
} from 'components/Wizard/types';

import routeMap from 'config/routes';
import DspConnection from 'components/Wizard/steps/DspConnection/DspConnection';
import { isArtistSoundCloudBypassEnabled } from 'helpers/artists';

const isStepComplete = (
  context: WizardContext,
  service: DspName,
  artistProfile: ArtistProfile & { spotify_profile_creation_pending?: boolean },
  connection?: Connection
) => {
  if (
    !connection &&
    context === 'label-release' &&
    service === 'spotify' &&
    artistProfile?.spotify_profile_creation_pending
  ) {
    return true;
  }

  return !!connection;
};

interface DspConnectionStepCreatorParams extends StepCreatorParams {
  service: DspName;
  singleStep: boolean;
}

/**
 * Returns a configuration object for one of the DSP connection step in the Wizard, used to
 * find and connect artist to profile for selected service.
 */

export const createDspConnectionStep = ({
  service,
  artistProfile,
  context,
  singleStep
}: DspConnectionStepCreatorParams): WizardStepConfig => {
  // NOTE: We are using the abbreviated formatted name for apple music
  const dspName = DIGITAL_SERVICE_PROVIDERS[service];
  const savedConnection = artistProfile?.connections?.find(({ dsp }) => dsp === service);
  const isBypassAllowed =
    service === 'soundcloud' && isArtistSoundCloudBypassEnabled(artistProfile);
  const isComplete = isStepComplete(context, service, artistProfile, savedConnection);

  const stepId = `${service}-connect`;
  const path = singleStep
    ? `${routeMap.artistProfileWizard.base}/${context}`
    : `${routeMap.artistProfileWizard.base}/${context}/${stepId}`;

  return {
    overviewRow: {
      title: dspName,
      subtitle: savedConnection
        ? `Artist's profile on ${dspName} is already connected.`
        : `Connect an existing ${dspName} profile.`,
      buttonText: savedConnection ? 'View Connection' : `Setup ${dspName}`
    },
    id: stepId,
    isRequired: !isBypassAllowed,
    isComplete,
    path,
    // eslint-disable-next-line react/display-name
    render: wizardProps => (
      <DspConnection artistProfile={artistProfile} service={service} {...wizardProps} />
    )
  };
};
